import type React from 'react';
import { reatomContext } from '@reatom/npm-react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { GridThemeProvider } from 'styled-bootstrap-grid';

import { Outlet } from '@/js/libs/router';
import { DatePickerStyles, CalendarStyles } from '@/js/libs/ui';
import { NotificationsContainer, injectNotificationsStyle } from '@/js/libs/ui/src/V2';
import { AuthProvider } from '@/js/libs/use-auth';
import { UseRequestProvider } from '@/js/libs/use-request';
import { TooltipProvider } from '@/js/libs/use-tooltip';

import 'src/api.ts';
import AnalyticsCollector from 'src/components/AnalyticsCollector.tsx';
import ErrorBoundary from 'src/components/ErrorBoundary.tsx';
import { RootErrorScreen } from 'src/components/ErrorScreen.tsx';
import { gridTheme } from 'src/components/GridTheme/index.tsx';
import { UsersnapScript } from 'src/components/UsersnapScript.tsx';
import { AuthContextProvider } from 'src/contexts/index.ts';
import i18n from 'src/i18n.ts';
import { CompanySwitchProvider } from 'src/modules/CompaniesSwitch/context.tsx';
import { ConfirmModalProvider } from 'src/modules/ConfirmModal/Context.tsx';
import { ConfirmationModal } from 'src/modules/ConfirmModal/index.tsx';
import { useFeatureToggleContext } from 'src/modules/FeatureToggle/index.ts';
import { useTranslateModeContext, TranslateModeOverlay } from 'src/modules/TranslateMode/index.ts';
import { useAuthVerificationContext } from 'src/scenes/Authentication/context.ts';
import { ctx, RouterDevtoolsSync } from 'src/store.tsx';
import { GlobalStyles } from 'src/theme/GlobalStyles.tsx';
import { CodeMirrorStyles } from 'src/theme/libs/CodeMirrorStyles.tsx';
import { DayjsInitializer } from './components/DayjsInitializer.tsx';
import { ErrorMessagesConfigurator } from './components/ErrorMessagesConfigurator.tsx';
import ExternalTokenListener from './components/ExternalTokenListener.tsx';
import { NumbersFormatContextProvider } from './components/FormattedNumber/context.ts';
import OneTimeLogout from './components/OneTimeLogout.tsx';
import { DEFAULT_DEBOUNCE_DELAY } from './constants.ts';
import { useConfirmationModalContext, useConfirmation } from './hooks/useConfirmation.tsx';
import { useRequestErrorNotification } from './hooks/useRequestErrorNotification.ts';

injectNotificationsStyle();

/*
  We need to have a separate wrapper because useRequestErrorNotification should be INSIDE I18nextProvider to have access to 't' function.
 */
const OnFailProvider = ({ children }: React.PropsWithChildren) => {
  const onFail = useRequestErrorNotification();
  return <UseRequestProvider value={{ onFail, debounce: DEFAULT_DEBOUNCE_DELAY }}>{children}</UseRequestProvider>;
};

const Application = () => (
  <>
    <GlobalStyles />
    <I18nextProvider i18n={i18n}>
      <NotificationsContainer />
      <DayjsInitializer />
      <ErrorBoundary errorScreenComponent={RootErrorScreen}>
        <AuthProvider>
          <reatomContext.Provider value={ctx}>
            <RouterDevtoolsSync />
            <AnalyticsCollector />
            <CompanySwitchProvider>
              <NumbersFormatContextProvider>
                <HelmetProvider>
                  <UsersnapScript />
                  <AuthContextProvider>
                    <ExternalTokenListener />
                    <OneTimeLogout />
                    <ErrorMessagesConfigurator />
                    <GridThemeProvider gridTheme={gridTheme}>
                      <>
                        <CodeMirrorStyles />
                        <DatePickerStyles />
                        <CalendarStyles />
                        <ConfirmModalProvider>
                          <TooltipProvider>
                            <useAuthVerificationContext.Provider>
                              <ConfirmationModal />
                              <OnFailProvider>
                                <useFeatureToggleContext.Provider>
                                  <useConfirmationModalContext.Provider>
                                    <useTranslateModeContext.Provider>
                                      <useConfirmation.Modal />
                                      <TranslateModeOverlay />
                                      <Outlet />
                                    </useTranslateModeContext.Provider>
                                  </useConfirmationModalContext.Provider>
                                </useFeatureToggleContext.Provider>
                              </OnFailProvider>
                            </useAuthVerificationContext.Provider>
                          </TooltipProvider>
                        </ConfirmModalProvider>
                      </>
                    </GridThemeProvider>
                  </AuthContextProvider>
                </HelmetProvider>
              </NumbersFormatContextProvider>
            </CompanySwitchProvider>
          </reatomContext.Provider>
        </AuthProvider>
      </ErrorBoundary>
    </I18nextProvider>
  </>
);

export default Application;
